import { normalizeFloorPlan3dCameraViewForUi } from '../data/floorPlan3dCameraViewOptions';
import { resolveFloorPlan3dPaddingRatio } from '../data/floorPlan3dPaddingRatios';

/**
 * @param {'standard' | 'hq'} quality
 * @returns {{ maxSide: number; jpegQuality: number }}
 */
function prepConfig(quality) {
  if (quality === 'hq') {
    return { maxSide: 1536, jpegQuality: 1 };
  }
  return { maxSide: 1025, jpegQuality: 0.95 };
}

/**
 * @param {number} width
 * @param {number} height
 * @param {number} maxSide
 * @returns {{ width: number; height: number }}
 */
function scaledDimensions(width, height, maxSide) {
  const max = Math.max(width, height);
  if (max <= maxSide) {
    return { width, height };
  }
  const scale = maxSide / max;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/**
 * @param {ImageBitmap} bitmap
 * @returns {Promise<void>}
 */
function closeBitmap(bitmap) {
  if (bitmap && typeof bitmap.close === 'function') {
    bitmap.close();
  }
}

/**
 * @param {File | Blob} file
 * @returns {Promise<ImageBitmap>}
 */
async function decodeFloorPlanBitmap(file) {
  if (typeof createImageBitmap !== 'function') {
    throw new Error('Image preparation is not supported in this browser.');
  }
  return createImageBitmap(file, { imageOrientation: 'from-image' });
}

/**
 * @param {HTMLCanvasElement} canvas
 * @param {number} jpegQuality
 * @returns {Promise<Blob>}
 */
function canvasToJpegBlob(canvas, jpegQuality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) reject(new Error('Could not encode floor plan image as JPEG.'));
        else resolve(blob);
      },
      'image/jpeg',
      jpegQuality,
    );
  });
}

/**
 * @param {ImageBitmap} bitmap
 * @param {number} drawWidth
 * @param {number} drawHeight
 * @param {number} paddingRatio
 * @param {boolean} skipPadding
 * @returns {HTMLCanvasElement}
 */
function renderPreparedCanvas(bitmap, drawWidth, drawHeight, paddingRatio, skipPadding) {
  const padLeft = skipPadding ? 0 : Math.round(drawWidth * paddingRatio);
  const padTop = skipPadding ? 0 : Math.round(drawHeight * paddingRatio);
  const canvasWidth = drawWidth + padLeft * 2;
  const canvasHeight = drawHeight + padTop * 2;

  const canvas = document.createElement('canvas');
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not create canvas context.');
  }

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvasWidth, canvasHeight);
  ctx.drawImage(bitmap, padLeft, padTop, drawWidth, drawHeight);
  return canvas;
}

/**
 * Resize, pad, and export a floor plan for 3D generation (no manual rotation).
 *
 * @param {File | Blob} file
 * @param {{
 *   cameraView?: string;
 *   quality?: 'standard' | 'hq';
 *   skipPadding?: boolean;
 * }} [options]
 * @returns {Promise<{
 *   blob: Blob;
 *   objectUrl: string;
 *   meta: {
 *     originalWidth: number;
 *     originalHeight: number;
 *     resizedWidth: number;
 *     resizedHeight: number;
 *     preparedWidth: number;
 *     preparedHeight: number;
 *     paddingRatio: number;
 *     paddingApplied: boolean;
 *     frontendPrepared: boolean;
 *     cameraView: string;
 *     mimeType: string;
 *     jpegQuality: number;
 *     outputSize: string;
 *   };
 * }>}
 */
export async function prepareFloorPlanTo3dInputImage(file, options = {}) {
  const quality = options.quality === 'hq' ? 'hq' : 'standard';
  const { maxSide, jpegQuality } = prepConfig(quality);
  const cameraView = normalizeFloorPlan3dCameraViewForUi(options.cameraView);
  const skipPadding = options.skipPadding === true;
  const paddingRatio = skipPadding ? 0 : resolveFloorPlan3dPaddingRatio(cameraView);

  const bitmap = await decodeFloorPlanBitmap(file);
  try {
    const originalWidth = bitmap.width;
    const originalHeight = bitmap.height;
    if (!originalWidth || !originalHeight) {
      throw new Error('Could not read image dimensions.');
    }

    const { width: resizedWidth, height: resizedHeight } = scaledDimensions(
      originalWidth,
      originalHeight,
      maxSide,
    );

    const canvas = renderPreparedCanvas(
      bitmap,
      resizedWidth,
      resizedHeight,
      paddingRatio,
      skipPadding,
    );
    const blob = await canvasToJpegBlob(canvas, jpegQuality);
    const objectUrl = URL.createObjectURL(blob);

    const preparedWidth = canvas.width;
    const preparedHeight = canvas.height;

    console.log('[FloorPlan3D Frontend Prepare]', {
      cameraView,
      paddingRatio,
      originalWidth,
      originalHeight,
      resizedWidth,
      resizedHeight,
      preparedWidth,
      preparedHeight,
      preparedBytes: blob.size,
      frontendPrepared: true,
      skipPadding,
    });

    return {
      blob,
      objectUrl,
      meta: {
        originalWidth,
        originalHeight,
        resizedWidth,
        resizedHeight,
        preparedWidth,
        preparedHeight,
        paddingRatio,
        paddingApplied: !skipPadding,
        frontendPrepared: true,
        cameraView,
        mimeType: 'image/jpeg',
        jpegQuality: Math.round(jpegQuality * 100),
        outputSize: `${preparedWidth}x${preparedHeight}`,
      },
    };
  } finally {
    await closeBitmap(bitmap);
  }
}
